import { type FC } from 'react';
import { Badge, Group, Loader, Paper, SimpleGrid, Text } from '@mantine/core';
import { IconLock, IconWallet } from '@tabler/icons-react';
import { Currency, getCurrencyIcon } from '~/entities/currency';
import { type PayoutTraderBalance } from '~/entities/payout';
import { formatAmount } from '~/shared/lib/utils/formatAmount';

interface TraderBalanceProps {
    balance?: PayoutTraderBalance;
    isLoading?: boolean;
}

export const TraderBalance: FC<TraderBalanceProps> = ({ balance, isLoading = false }) => {
    const currency = balance?.currency ?? Currency.CURRENCY_RUB;
    const dimmedIconColor = 'var(--mantine-color-dimmed)';

    return (
        <Paper
            w='100%'
            p='sm'
            radius='md'
            bg='var(--mantine-color-default)'
            withBorder
        >
            <Group
                justify='space-between'
                align='center'
                wrap='nowrap'
                mb='xs'
            >
                <Group
                    gap={6}
                    wrap='nowrap'
                >
                    <IconWallet
                        size={16}
                        color={dimmedIconColor}
                    />
                    <Text
                        size='sm'
                        fw={500}
                    >
                        Баланс
                    </Text>
                </Group>

                <Badge
                    size='sm'
                    variant='default'
                    leftSection={getCurrencyIcon(currency)}
                >
                    RUB
                </Badge>
            </Group>

            {isLoading && (
                <Group gap='xs'>
                    <Loader size='xs' />
                    <Text size='sm'>Загрузка баланса</Text>
                </Group>
            )}

            {!isLoading && balance && (
                <SimpleGrid
                    cols={{ base: 1, xs: 2 }}
                    spacing='xs'
                >
                    <Paper
                        p='xs'
                        radius='sm'
                        bg='var(--mantine-color-body)'
                        withBorder
                    >
                        <Group
                            justify='space-between'
                            align='center'
                            wrap='nowrap'
                        >
                            <Text
                                size='xs'
                                c='dimmed'
                            >
                                Доступно
                            </Text>
                            <IconWallet
                                size={14}
                                color={dimmedIconColor}
                            />
                        </Group>
                        <Group
                            gap={4}
                            mt={2}
                            wrap='nowrap'
                        >
                            <Text
                                size='lg'
                                fw={600}
                                lh={1.15}
                            >
                                {formatAmount(balance.value?.value)}
                            </Text>
                            {getCurrencyIcon(currency)}
                        </Group>
                    </Paper>

                    <Paper
                        p='xs'
                        radius='sm'
                        bg='var(--mantine-color-body)'
                        withBorder
                    >
                        <Group
                            justify='space-between'
                            align='center'
                            wrap='nowrap'
                        >
                            <Text
                                size='xs'
                                c='dimmed'
                            >
                                Заморожено
                            </Text>
                            <IconLock
                                size={14}
                                color={dimmedIconColor}
                            />
                        </Group>
                        <Group
                            gap={4}
                            mt={2}
                            wrap='nowrap'
                        >
                            <Text
                                size='lg'
                                fw={600}
                                lh={1.15}
                            >
                                {formatAmount(balance.hold?.value)}
                            </Text>
                            {getCurrencyIcon(currency)}
                        </Group>
                    </Paper>
                </SimpleGrid>
            )}

            {!isLoading && !balance && (
                <Text
                    size='sm'
                    c='dimmed'
                    pt={2}
                >
                    Нет данных
                </Text>
            )}
        </Paper>
    );
};
